import { useCallback, useState } from "react";

interface ControllableExpansionParams {
  /** Set by a caller that drives the expanded modal itself. */
  isExpanded?: boolean;
  defaultExpanded: boolean;
  onExpandedClose?: () => void;
}

/**
 * The expanded-modal state of a chart or table message.
 *
 * Uncontrolled by default, since a message in the chat opens its own modal.
 * A caller that outlives one opening — a list that previews a row through a
 * hidden renderer — passes `isExpanded` instead, so the modal can close and
 * reopen without the renderer being unmounted in between, which is what would
 * otherwise leave the page unclickable behind it.
 */
export const useControllableExpansion = ({
  isExpanded,
  defaultExpanded,
  onExpandedClose
}: ControllableExpansionParams) => {
  const [isExpandedLocally, setIsExpandedLocally] = useState(defaultExpanded);
  const isExpandedOpen = isExpanded ?? isExpandedLocally;

  const expand = useCallback(() => setIsExpandedLocally(true), []);

  const setIsExpandedOpen = useCallback(
    (open: React.SetStateAction<boolean>) => {
      const nextValue =
        typeof open === "function" ? open(isExpandedOpen) : open;
      setIsExpandedLocally(nextValue);
      if (!nextValue) onExpandedClose?.();
    },
    [isExpandedOpen, onExpandedClose]
  );

  return { isExpandedOpen, expand, setIsExpandedOpen };
};

/**
 * Whether a message can still be scheduled.
 *
 * `isSchedulable` is the row's own flag, true only on the originating message.
 * `isReplayable` is the same answer resolved server-side across the lineage, so
 * a message the scheduler or Run now produced keeps the Schedule button. The
 * `scheduleId` fallback covers a message delivered before the server could
 * resolve its lineage.
 */
export const resolveIsSchedulable = (
  isSchedulable: boolean,
  isReplayable: boolean,
  scheduleId: string | null
) => isSchedulable || isReplayable || scheduleId !== null;
